<ng-template #noPermissions>
  <permissions-denied class="w-full"></permissions-denied>
</ng-template>

<div
  class="absolute inset-0 flex flex-col min-w-0 overflow-hidden"
  *requiresPermissionCanView="['bulkenterquoterequests']; op: 'AND'; permissionDenied: noPermissions">
  <!-- Fixed Header -->
  <div class="flex-shrink-0 z-10 border-b-2 border-gray-200 px-6 sm:px-10 py-4 bg-white dark:bg-gray-900">
    <h2 class="text-2xl font-bold tracking-tight leading-7">Process Missing Renewals</h2>
  </div>

  <!-- Scrollable Content Area - pb-20 on desktop for fixed action bar -->
  <div class="scrollable-content flex-1 overflow-y-auto overflow-x-hidden min-h-0 md:pb-20">
    <form [formGroup]="form" class="w-full relative px-6 sm:px-10 py-4">
      @if (isUiFieldsLoading()) {
        <ui-loading-state [loading]="true" loadingText="Loading field metadata..." emptyText=""></ui-loading-state>
      } @else {
        <div class="flex flex-col lg:flex-row gap-6 w-full md:mb-10">
          <!-- Left Column: Order Numbers -->
          <div class="w-full lg:w-5/12 cipx-form-section">
            <div class="cipx-form-section-title">Order Numbers</div>
            <div class="cipx-form-form-group">
              <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                <textarea
                  matInput
                  [rows]="14"
                  [spellcheck]="false"
                  placeholder="Enter one order number per line"
                  formControlName="orderNumbers"></textarea>
                <mat-hint>One per line.</mat-hint>
                @if (form.controls.orderNumbers.touched && form.controls.orderNumbers.hasError('orderNumbersEmpty')) {
                  <mat-error>Please enter at least one order number.</mat-error>
                }
                @if (form.controls.orderNumbers.touched && form.controls.orderNumbers.hasError('invalidOrderNumbers')) {
                  <mat-error>Some order numbers are invalid. Fix the red chips below.</mat-error>
                }
              </mat-form-field>
            </div>

            @let analysis = analyzeOrderNumbers(form.controls.orderNumbers.value);
            @if (analysis.valid.length || analysis.invalid.length) {
              <div class="mt-3 flex flex-col gap-3">
                @if (analysis.valid.length) {
                  <div>
                    <div class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Valid ({{ analysis.valid.length }})
                    </div>
                    <mat-chip-set aria-label="Valid order numbers">
                      @for (v of analysis.valid; track v) {
                        <mat-chip color="primary" highlighted>{{ v }}</mat-chip>
                      }
                    </mat-chip-set>
                  </div>
                }

                @if (analysis.invalid.length) {
                  <div>
                    <div class="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                      Invalid ({{ analysis.invalid.length }})
                    </div>
                    <mat-chip-set aria-label="Invalid order numbers">
                      @for (i of analysis.invalid; track i.value + ':' + i.reason) {
                        <mat-chip color="warn" highlighted>
                          {{ i.value }}
                          @if (i.reason === 'duplicate') {
                            (duplicate)
                          } @else {
                            (invalid format)
                          }
                        </mat-chip>
                      }
                    </mat-chip-set>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Right Column: Options + Set Values -->
          <div class="w-full lg:w-7/12">
            @if (uiFieldsNetworkError() || (uiFieldsResponse() && uiFieldsResponse()!.success === false)) {
              <div class="cipx-form-section mb-4 border-2 border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800">
                <div class="cipx-form-section-title text-red-800 dark:text-red-200">Unable to load fields</div>
                <div class="text-sm text-red-700 dark:text-red-200 mt-2">Ut oh, there was a problem!</div>
              </div>
            }

            <div class="cipx-form-section">
              <div class="cipx-form-section-title">Options</div>
              <div class="flex flex-col gap-2 mt-2">
                <mat-checkbox formControlName="quoteInstantPrices" color="primary">Quote Instant Prices</mat-checkbox>
                <mat-checkbox formControlName="copyBondNumber" color="primary">Copy Bond Number</mat-checkbox>
                <mat-checkbox formControlName="skipAssignments" color="primary">Skip Assignments</mat-checkbox>
              </div>
            </div>

            <div class="cipx-form-section">
              <div class="flex items-center justify-between gap-4">
                <div class="cipx-form-section-title">Set Values on Quote Request</div>
                <cipx-button
                  [type]="'secondary'"
                  [icon]="'heroicons_outline:plus'"
                  (buttonClick)="addRow('quote_request')">
                  Add Field
                </cipx-button>
              </div>

              <div class="mt-4" formArrayName="setQuoteRequestValues">
                @for (row of quoteRequestRows.controls; track $index) {
                  <div class="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start mb-4" [formGroupName]="$index">
                    <mat-form-field
                      class="w-full lg:col-span-6"
                      [subscriptSizing]="'dynamic'"
                      hideRequiredMarker="true">
                      <mat-label>Field</mat-label>
                      <mat-select formControlName="field" (selectionChange)="onFieldChanged('quote_request', $index)">
                        @for (f of quoteRequestFields(); track f.key) {
                          <mat-option [value]="f.key">{{ f.label }} ({{ f.type }})</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>

                    <div class="w-full lg:col-span-5">
                      @switch (getFieldType('quote_request', row.controls.field.value)) {
                        @case ('boolean') {
                          <mat-checkbox formControlName="value" color="primary" class="mt-2">True / False</mat-checkbox>
                        }
                        @case ('integer') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="number" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('float') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="number" step="any" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('date') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="date" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('datetime') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="datetime-local" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('timestamp') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="datetime-local" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('json') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value (JSON)</mat-label>
                            <textarea
                              matInput
                              [rows]="3"
                              formControlName="value"
                              placeholder='{"key":"value"}'></textarea>
                          </mat-form-field>
                        }
                        @case ('text') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <textarea matInput [rows]="3" formControlName="value"></textarea>
                          </mat-form-field>
                        }
                        @default {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="text" formControlName="value" />
                          </mat-form-field>
                        }
                      }
                    </div>

                    <div class="w-full lg:col-span-1 flex justify-end lg:justify-start">
                      <button
                        type="button"
                        mat-icon-button
                        aria-label="Remove field"
                        (click)="removeRow('quote_request', $index)">
                        <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>

            <div class="cipx-form-section">
              <div class="flex items-center justify-between gap-4">
                <div class="cipx-form-section-title">Set Values on Quote Request Bond</div>
                <cipx-button
                  [type]="'secondary'"
                  [icon]="'heroicons_outline:plus'"
                  (buttonClick)="addRow('quote_request_bond')">
                  Add Field
                </cipx-button>
              </div>

              <div class="mt-4" formArrayName="setQuoteRequestBondValues">
                @for (row of quoteRequestBondRows.controls; track $index) {
                  <div class="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start mb-4" [formGroupName]="$index">
                    <mat-form-field
                      class="w-full lg:col-span-6"
                      [subscriptSizing]="'dynamic'"
                      hideRequiredMarker="true">
                      <mat-label>Field</mat-label>
                      <mat-select
                        formControlName="field"
                        (selectionChange)="onFieldChanged('quote_request_bond', $index)">
                        @for (f of quoteRequestBondFields(); track f.key) {
                          <mat-option [value]="f.key">{{ f.label }} ({{ f.type }})</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>

                    <div class="w-full lg:col-span-5">
                      @switch (getFieldType('quote_request_bond', row.controls.field.value)) {
                        @case ('boolean') {
                          <mat-checkbox formControlName="value" color="primary" class="mt-2">True / False</mat-checkbox>
                        }
                        @case ('integer') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="number" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('float') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="number" step="any" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('date') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="date" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('datetime') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="datetime-local" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('timestamp') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="datetime-local" formControlName="value" />
                          </mat-form-field>
                        }
                        @case ('json') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value (JSON)</mat-label>
                            <textarea
                              matInput
                              [rows]="3"
                              formControlName="value"
                              placeholder='{"key":"value"}'></textarea>
                          </mat-form-field>
                        }
                        @case ('text') {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <textarea matInput [rows]="3" formControlName="value"></textarea>
                          </mat-form-field>
                        }
                        @default {
                          <mat-form-field class="w-full" [subscriptSizing]="'dynamic'" hideRequiredMarker="true">
                            <mat-label>Value</mat-label>
                            <input matInput type="text" formControlName="value" />
                          </mat-form-field>
                        }
                      }
                    </div>

                    <div class="w-full lg:col-span-1 flex justify-end lg:justify-start">
                      <button
                        type="button"
                        mat-icon-button
                        aria-label="Remove field"
                        (click)="removeRow('quote_request_bond', $index)">
                        <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <cipx-action-bar [sticky]="true" [removeStickyOnMobile]="true" justify="start">
        <cipx-primary-button
          [disabled]="form.invalid || processMissingService.isLoading() || !isUiFieldsReady()"
          [icon]="'heroicons_outline:play'"
          (buttonClick)="onProcess()">
          Process
        </cipx-primary-button>
        <cipx-cancel-button (buttonClick)="onCancel()" [disabled]="processMissingService.isLoading()">
          Clear
        </cipx-cancel-button>
      </cipx-action-bar>

      <!-- Response messages section -->
      <div class="mt-4">
        @if (lastScheduleResponse() && !lastScheduleResponse()!.data?.unable_to_process?.length) {
          <div
            class="cipx-form-section mb-4 border-2 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
            <div class="cipx-form-section-title text-green-800 dark:text-green-200">Scheduled</div>
            <div class="mt-3">
              @let completedList =
                lastScheduledOrderNumbers().length ? lastScheduledOrderNumbers() : lastSubmittedOrderNumbers();
              <div class="text-sm font-medium text-green-700 dark:text-green-200 mb-1">
                Scheduled Order Numbers ({{ completedList.length }})
              </div>
              <div class="flex flex-wrap gap-2 text-sm">
                @for (orderNumber of completedList; track orderNumber) {
                  <span
                    class="inline-flex rounded border border-green-200 bg-white/60 dark:bg-transparent px-2 py-0.5 font-mono text-green-900 dark:text-green-100">
                    {{ orderNumber }}
                  </span>
                }
              </div>
            </div>
          </div>
        }

        @if (lastScheduleNetworkError()) {
          <div class="cipx-form-section mb-4 border-2 border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800">
            <div class="cipx-form-section-title text-red-800 dark:text-red-200">Request Failed</div>
            <div class="text-sm text-red-700 dark:text-red-200 mt-2">
              The request failed to submit. Check your network and try again.
            </div>
          </div>
        }

        @if (lastScheduleResponse() && lastScheduleResponse()!.data?.unable_to_process?.length) {
          <div class="cipx-form-section mb-4 border-2 border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800">
            <div class="cipx-form-section-title text-red-800 dark:text-red-200">Unable to process</div>
            <div class="mt-3">
              <div class="text-sm font-medium text-red-700 dark:text-red-200 mb-1">
                Failed Order Numbers ({{ lastScheduleResponse()!.data!.unable_to_process.length }})
              </div>
              <div class="flex flex-wrap gap-2 text-sm">
                @for (
                  item of lastScheduleResponse()!.data!.unable_to_process;
                  track item.order_number + ':' + item.error
                ) {
                  <span
                    class="inline-flex rounded border border-red-200 bg-white/60 dark:bg-transparent px-2 py-0.5 font-mono text-red-900 dark:text-red-100">
                    {{ item.order_number }} — {{ item.error }}
                  </span>
                }
              </div>
            </div>
          </div>
        }
      </div>

      @if (processMissingService.isLoading()) {
        <div class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-gray-900/60">
          <ui-loading-state [loading]="true" loadingText="Processing..." emptyText=""></ui-loading-state>
        </div>
      }
    </form>
  </div>
</div>
